<template>
    <DataManagementLayout>
      <div class="history-data-page">
        <!-- 页面标题和控制区域 -->
        <div class="mb-8">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-bold text-gray-800">桥梁检测</h2>
            <div class="flex items-center space-x-3">
              <!-- 桥梁选择器 -->
              <el-select
                v-model="selectedBridgeId"
                placeholder="请选择桥梁"
                filterable
                @change="onBridgeChange"
                class="w-48"
              >
                <el-option
                  v-for="bridge in bridgeOptions"
                  :key="bridge.id"
                  :label="bridge.bridgeName"
                  :value="bridge.id"
                />
              </el-select>
              
              <!-- 时间范围选择 -->
              <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                value-format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                @change="onTimeRangeChange"
                class="w-80"
              />
              
              <!-- 刷新按钮 -->
              <el-button 
                type="primary" 
                icon="el-icon-refresh" 
                @click="refreshData"
                :loading="loading"
                size="small"
              >
                刷新
              </el-button>
            </div>
          </div>
        </div>
  
        <!-- 标签页区域 -->
        <div class="mb-8">
          <el-tabs v-model="activeTab" @tab-click="handleTabClick">
  
            <!-- 流速计标签页 -->
            <el-tab-pane v-if="shouldShowFlowmeter" label="流速计" name="flowmeter">
              <div class="mt-4">
                <!-- 图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="flowmeterMetrics" :loading="chartLoading.flowmeter" />
                    <div class="flex justify-between items-center mb-4">
                      <div class="flex flex-col space-y-2">
                        <h3 class="text-lg font-semibold text-gray-800">流速历史趋势</h3>
                        <!-- 时间粒度切换器 -->
                        <div class="flex items-center space-x-2">
                          <span class="text-sm text-gray-600">时间粒度：</span>
                          <el-radio-group v-model="flowmeterGranularity" @change="onFlowmeterGranularityChange" size="small">
                            <el-radio-button label="hour">按小时</el-radio-button>
                            <el-radio-button label="day">按天</el-radio-button>
                          </el-radio-group>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2 text-sm text-gray-600">
                        <span>更新时间：{{ updateTime }}</span>
                        <el-button
                          type="text"
                          icon="el-icon-refresh"
                          @click="refreshChartData('flowmeter')"
                          :loading="chartLoading.flowmeter"
                          size="mini"
                        >
                          刷新
                        </el-button>
                      </div>
                    </div>
                    <div ref="flowmeterChart" class="h-80" v-loading="chartLoading.flowmeter"></div>
                  </div>
                </div>
  
                <!-- 数据表格区域 -->
                <div class="bg-white rounded-lg shadow-md p-6" style="display: none;">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">流速历史数据</h3>
                  </div>
                  
                  <el-table
                    :data="tableData.flowmeter"
                    :loading="tableLoading.flowmeter"
                    stripe
                    border
                    height="400"
                    class="w-full"
                  >
                    <el-table-column prop="dataTime" label="时间" width="180">
                      <template slot-scope="scope">
                        {{ formatDataTime(scope.row.dataTime) }}
                      </template>
                    </el-table-column>
                    <el-table-column prop="clientIp" label="设备IP" width="150" show-overflow-tooltip>
                    </el-table-column>
                    <el-table-column prop="flowSpeed" label="流速值" width="120">
                      <template slot-scope="scope">
                        {{ formatFlowmeterData(scope.row) }}
                      </template>
                    </el-table-column>
                  </el-table>
                  
                  <!-- 分页 -->
                  <div class="flex justify-between items-center mt-4">
                    <div class="text-sm text-gray-500">
                      共 {{ pagination.flowmeter.total }} 条记录
                    </div>
                    <el-pagination
                      @size-change="handleSizeChange('flowmeter', $event)"
                      @current-change="handleCurrentChange('flowmeter', $event)"
                      :current-page="pagination.flowmeter.pageNum"
                      :page-sizes="[10, 20, 50, 100]"
                      :page-size="pagination.flowmeter.pageSize"
                      :total="pagination.flowmeter.total"
                      layout="total, sizes, prev, pager, next, jumper"
                    >
                    </el-pagination>
                  </div>
                </div>
              </div>
            </el-tab-pane>
  
            <!-- 风速标签页 -->
            <el-tab-pane v-if="shouldShowAnemometer" label="风速" name="anemometer">
              <div class="mt-4">
                <!-- 图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="anemometerMetrics" :loading="chartLoading.anemometer" />
                    <div class="flex justify-between items-center mb-4">
                      <div class="flex flex-col space-y-2">
                        <h3 class="text-lg font-semibold text-gray-800">风速历史趋势</h3>
                        <!-- 时间粒度切换器 -->
                        <div class="flex items-center space-x-2">
                          <span class="text-sm text-gray-600">时间粒度：</span>
                          <el-radio-group v-model="anemometerGranularity" @change="onAnemometerGranularityChange" size="small">
                            <el-radio-button label="hour">按小时</el-radio-button>
                            <el-radio-button label="day">按天</el-radio-button>
                          </el-radio-group>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2 text-sm text-gray-600">
                        <span>更新时间：{{ updateTime }}</span>
                        <el-button
                          type="text"
                          icon="el-icon-refresh"
                          @click="refreshChartData('anemometer')"
                          :loading="chartLoading.anemometer"
                          size="mini"
                        >
                          刷新
                        </el-button>
                      </div>
                    </div>
                    <div ref="anemometerChart" class="h-80" v-loading="chartLoading.anemometer"></div>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <!-- 能见度计标签页 -->
            <el-tab-pane v-if="shouldShowVisibility" label="能见度计" name="visibility">
              <div class="mt-4">
                <!-- 图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="visibilityMetrics" :loading="chartLoading.visibility" />
                    <div class="flex justify-between items-center mb-4">
                      <div class="flex flex-col space-y-2">
                        <h3 class="text-lg font-semibold text-gray-800">能见度历史趋势</h3>
                        <!-- 时间粒度切换器 -->
                        <div class="flex items-center space-x-2">
                          <span class="text-sm text-gray-600">时间粒度：</span>
                          <el-radio-group v-model="visibilityGranularity" @change="onVisibilityGranularityChange" size="small">
                            <el-radio-button label="hour">按小时</el-radio-button>
                            <el-radio-button label="day">按天</el-radio-button>
                          </el-radio-group>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2 text-sm text-gray-600">
                        <span>更新时间：{{ updateTime }}</span>
                        <el-button
                          type="text"
                          icon="el-icon-refresh"
                          @click="refreshChartData('visibility')"
                          :loading="chartLoading.visibility"
                          size="mini"
                        >
                          刷新
                        </el-button>
                      </div>
                    </div>
                    <div ref="visibilityChart" class="h-80" v-loading="chartLoading.visibility"></div>
                  </div>
                </div>
  
                <!-- 数据表格区域 -->
                <div class="bg-white rounded-lg shadow-md p-6" style="display: none;">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">能见度历史数据</h3>
                  </div>
                  
                  <el-table
                    :data="tableData.visibility"
                    :loading="tableLoading.visibility"
                    stripe
                    border
                    height="400"
                    class="w-full"
                  >
                    <el-table-column prop="dataTime" label="时间" width="180">
                      <template slot-scope="scope">
                        {{ formatDataTime(scope.row.dataTime) }}
                      </template>
                    </el-table-column>
                    <el-table-column prop="deviceCode" label="设备代码" width="200" show-overflow-tooltip>
                    </el-table-column>
                    <el-table-column prop="oneMinVisibilityM" label="1分钟平均值(米)" width="140">
                      <template slot-scope="scope">
                        {{ formatVisibilityValue(scope.row.oneMinVisibilityM) }}
                      </template>
                    </el-table-column>
                    <el-table-column prop="fifteenSecVisibilityM" label="15秒瞬时值(米)" width="140">
                      <template slot-scope="scope">
                        {{ formatVisibilityValue(scope.row.fifteenSecVisibilityM) }}
                      </template>
                    </el-table-column>
                  </el-table>
                  
                  <!-- 分页 -->
                  <div class="flex justify-between items-center mt-4">
                    <div class="text-sm text-gray-500">
                      共 {{ pagination.visibility.total }} 条记录
                    </div>
                    <el-pagination
                      @size-change="handleSizeChange('visibility', $event)"
                      @current-change="handleCurrentChange('visibility', $event)"
                      :current-page="pagination.visibility.pageNum"
                      :page-sizes="[10, 20, 50, 100]"
                      :page-size="pagination.visibility.pageSize"
                      :total="pagination.visibility.total"
                      layout="total, sizes, prev, pager, next, jumper"
                    >
                    </el-pagination>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <!-- 张力计标签页 -->
            <el-tab-pane v-if="shouldShowTensionmeter" label="张力计" name="tensionmeter">
              <div class="mt-4">
                <!-- 图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="tensionmeterMetrics" :loading="chartLoading.tensionmeter" />
                    <div class="flex justify-between items-center mb-4">
                      <div class="flex flex-col space-y-2">
                        <h3 class="text-lg font-semibold text-gray-800">张力历史趋势</h3>
                        <!-- 时间粒度切换器 -->
                        <div class="flex items-center space-x-2">
                          <span class="text-sm text-gray-600">时间粒度：</span>
                          <el-radio-group v-model="tensionmeterGranularity" @change="onTensionmeterGranularityChange" size="small">
                            <el-radio-button label="hour">按小时</el-radio-button>
                            <el-radio-button label="day">按天</el-radio-button>
                          </el-radio-group>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2 text-sm text-gray-600">
                        <span>更新时间：{{ updateTime }}</span>
                        <el-button
                          type="text"
                          icon="el-icon-refresh"
                          @click="refreshChartData('tensionmeter')"
                          :loading="chartLoading.tensionmeter"
                          size="mini"
                        >
                          刷新
                        </el-button>
                      </div>
                    </div>
                    <div ref="tensionmeterChart" class="h-80" v-loading="chartLoading.tensionmeter"></div>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <!-- 倾角计标签页 -->
            <el-tab-pane v-if="shouldShowInclinometer" label="倾角计" name="inclinometer">
              <div class="mt-4">
                <!-- 控制栏 -->
                <div class="mb-4 flex justify-between items-center">
                  <div class="flex items-center space-x-3">
                    <span class="text-sm font-medium text-gray-700">时间粒度：</span>
                    <el-radio-group v-model="inclinometerGranularity" @change="onInclinometerGranularityChange" size="small">
                      <el-radio-button label="hour">按小时</el-radio-button>
                      <el-radio-button label="day">按天</el-radio-button>
                    </el-radio-group>
                  </div>
                  <div class="flex items-center space-x-2 text-sm text-gray-600">
                    <span>更新时间：{{ updateTime }}</span>
                    <el-button
                      type="text"
                      icon="el-icon-refresh"
                      @click="refreshChartData('inclinometer')"
                      :loading="chartLoading.inclinometer"
                      size="mini"
                    >
                      刷新
                    </el-button>
                  </div>
                </div>

                <!-- 加速度图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="inclinometerMetrics" :loading="chartLoading.inclinometer" />
                    <div class="flex items-center mb-4">
                      <i class="el-icon-data-line text-blue-500 text-xl mr-2"></i>
                      <h3 class="text-lg font-semibold text-gray-800">三轴加速度监测</h3>
                      <span class="ml-3 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">单位: g</span>
                    </div>
                    <div ref="inclinometerAccChart" class="h-80" v-loading="chartLoading.inclinometer"></div>
                  </div>
                </div>

                <!-- 角度图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <div class="flex items-center mb-4">
                      <i class="el-icon-data-analysis text-purple-500 text-xl mr-2"></i>
                      <h3 class="text-lg font-semibold text-gray-800">三轴角度监测</h3>
                      <span class="ml-3 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">单位: °</span>
                    </div>
                    <div ref="inclinometerAngleChart" class="h-80" v-loading="chartLoading.inclinometer"></div>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <!-- 位移计标签页 -->
            <el-tab-pane v-if="shouldShowDisplacement" label="位移计" name="displacement">
              <div class="mt-4">
                <!-- 控制栏 -->
                <div class="mb-4 flex justify-between items-center">
                  <div class="flex items-center space-x-3">
                    <span class="text-sm font-medium text-gray-700">时间粒度：</span>
                    <el-radio-group v-model="displacementGranularity" @change="onDisplacementGranularityChange" size="small">
                      <el-radio-button label="hour">按小时</el-radio-button>
                      <el-radio-button label="day">按天</el-radio-button>
                    </el-radio-group>
                  </div>
                  <div class="flex items-center space-x-2 text-sm text-gray-600">
                    <span>更新时间：{{ updateTime }}</span>
                    <el-button
                      type="text"
                      icon="el-icon-refresh"
                      @click="refreshChartData('displacement')"
                      :loading="chartLoading.displacement"
                      size="mini"
                    >
                      刷新
                    </el-button>
                  </div>
                </div>

                <!-- X轴位移图表 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="displacementMetrics" :loading="chartLoading.displacement" />
                    <div class="flex items-center mb-4">
                      <i class="el-icon-right text-blue-500 text-xl mr-2"></i>
                      <h3 class="text-lg font-semibold text-gray-800">X轴位移监测</h3>
                      <span class="ml-3 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">单位: mm</span>
                    </div>
                    <div ref="displacementXChart" class="h-80" v-loading="chartLoading.displacement"></div>
                  </div>
                </div>

                <!-- Y轴位移图表 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <div class="flex items-center mb-4">
                      <i class="el-icon-top text-green-500 text-xl mr-2"></i>
                      <h3 class="text-lg font-semibold text-gray-800">Y轴位移监测</h3>
                      <span class="ml-3 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">单位: mm</span>
                    </div>
                    <div ref="displacementYChart" class="h-80" v-loading="chartLoading.displacement"></div>
                  </div>
                </div>

                <!-- 绝对位移图表 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <div class="flex items-center mb-4">
                      <i class="el-icon-position text-orange-500 text-xl mr-2"></i>
                      <h3 class="text-lg font-semibold text-gray-800">绝对位移监测</h3>
                      <span class="ml-3 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">单位: mm</span>
                    </div>
                    <div ref="displacementAbsChart" class="h-80" v-loading="chartLoading.displacement"></div>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <!-- 净高标签页 -->
            <el-tab-pane v-if="shouldShowClearance" label="净高" name="clearance">
              <div class="mt-4">
                <!-- 图表区域 -->
                <div class="mb-6">
                  <div class="bg-white rounded-lg shadow-md p-6">
                    <MetricCards :items="clearanceMetrics" :loading="chartLoading.clearance" />
                    <div class="flex justify-between items-center mb-4">
                      <div class="flex flex-col space-y-2">
                        <h3 class="text-lg font-semibold text-gray-800">净高历史趋势</h3>
                        <span
                          v-if="clearanceFallback"
                          class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded"
                        >
                          当前时间段无实测数据，展示桥梁净高校准值
                        </span>
                        <!-- 时间粒度切换器 -->
                        <div class="flex items-center space-x-2">
                          <span class="text-sm text-gray-600">时间粒度：</span>
                          <el-radio-group v-model="clearanceGranularity" @change="onClearanceGranularityChange" size="small">
                            <el-radio-button label="hour">按小时</el-radio-button>
                            <el-radio-button label="day">按天</el-radio-button>
                          </el-radio-group>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2 text-sm text-gray-600">
                        <span>更新时间：{{ updateTime }}</span>
                        <el-button
                          type="text"
                          icon="el-icon-download"
                          @click="exportChartCsv('clearance', '桥梁净高历史数据')"
                          size="mini"
                        >
                          导出
                        </el-button>
                        <el-button
                          type="text"
                          icon="el-icon-refresh"
                          @click="refreshChartData('clearance')"
                          :loading="chartLoading.clearance"
                          size="mini"
                        >
                          刷新
                        </el-button>
                      </div>
                    </div>
                    <div ref="clearanceChart" class="h-80" v-loading="chartLoading.clearance"></div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </DataManagementLayout>
  </template>
  
  <script>
  import DataManagementLayout from '@/components/DataManagementLayout.vue'
  import MetricCards from '@/components/common/MetricCards.vue'
  import { getAllBridges } from '@/api/bridge'
  import { getDevicesByBridge } from '@/api/device'
  import { getWaterLevelHistoryData, getVisibilityHistoryData, getVisibilityStatistics, getAnemometerStatistics, getFlowmeterHistoryData, getFlowmeterStatistics, getWaterLevelStatistics, getTensionmeterStatistics, getInclinometerAccelerationStatistics, getDisplacementAbsoluteStatistics, getClearanceStatistics } from '@/api/device-history'
  import { getWaterLevelRealTimeData } from '@/api/device-realtime'
  import bridgeStateManager from '@/utils/bridgeStateManager'
  import * as echarts from 'echarts'
  
  export default {
    name: 'HistoryData',
    components: {
      DataManagementLayout,
      MetricCards
    },
    data() {
      return {
        selectedBridgeId: bridgeStateManager.getCurrentBridgeId(),
        timeRange: this.getDefaultTimeRange(),
        bridgeOptions: bridgeStateManager.getBridgeList(),
        activeTab: 'flowmeter',
        visibilityGranularity: 'hour', // 能见度时间粒度：hour（按小时）、day（按天）
        anemometerGranularity: 'hour', // 风速时间粒度：hour（按小时）、day（按天）
        flowmeterGranularity: 'hour', // 流速计时间粒度：hour（按小时）、day（按天）
        waterLevelGranularity: 'hour', // 水位计时间粒度：hour（按小时）、day（按天）
        tensionmeterGranularity: 'hour', // 张力计时间粒度：hour（按小时）、day（按天）
        inclinometerGranularity: 'hour', // 倾角计时间粒度：hour（按小时）、day（按天）
        displacementGranularity: 'hour', // 位移计时间粒度：hour（按小时）、day（按天）
        clearanceGranularity: 'hour', // 净高时间粒度：hour（按小时）、day（按天）
        loading: false,
        
        // 设备存在标志（用于判断是否显示对应的标签页）
        hasWaterLevelDevice: false,
        hasAnemometerDevice: false,
        hasVisibilityDevice: false,
        hasFlowmeterDevice: false,
        hasTensionmeterDevice: false,
        hasInclinometerDevice: false,
        hasDisplacementDevice: false,
        hasClearanceDevice: false,
        clearanceFallback: false,
        chartLoading: {
          waterLevel: false,
          anemometer: false,
          visibility: false,
          flowmeter: false,
          tensionmeter: false,
          inclinometer: false,
          displacement: false,
          clearance: false
        },
        tableLoading: {
          waterLevel: false,
          visibility: false,
          flowmeter: false,
          tensionmeter: false,
          inclinometer: false,
          displacement: false
        },
        updateTime: '',
        
        // 图表实例
        charts: {
          waterLevel: null,
          anemometer: null,
          visibility: null,
          flowmeter: null,
          tensionmeter: null,
          inclinometerAcc: null,
          inclinometerAngle: null,
          displacementX: null,
          displacementY: null,
          displacementAbs: null,
          clearance: null
        },
        
        // 统计数据原始时间序列（用于计算指标卡）
        seriesData: {
          flowmeter: [],
          visibility: [],
          anemometer: [],
          tensionmeter: [],
          inclinometer: [],
          displacement: [],
          clearance: []
        },
        
        // 各标签页指标卡数据（最新/最大/最小/平均/数据量）
        flowmeterMetrics: [],
        visibilityMetrics: [],
        anemometerMetrics: [],
        tensionmeterMetrics: [],
        inclinometerMetrics: [],
        displacementMetrics: [],
        clearanceMetrics: [],
        
        // 表格数据
        tableData: {
          waterLevel: [],
          visibility: [],
          flowmeter: [],
          tensionmeter: [],
          inclinometer: [],
          displacement: []
        },
        
        // 分页信息
        pagination: {
          waterLevel: {
            pageNum: 1,
            pageSize: 20,
            total: 0
          },
          visibility: {
            pageNum: 1,
            pageSize: 20,
            total: 0
          },
          flowmeter: {
            pageNum: 1,
            pageSize: 20,
            total: 0
          },
          tensionmeter: {
            pageNum: 1,
            pageSize: 20,
            total: 0
          },
          inclinometer: {
            pageNum: 1,
            pageSize: 20,
            total: 0
          },
          displacement: {
            pageNum: 1,
            pageSize: 20,
            total: 0
          }
        }
      }
    },
    
    computed: {
      // 是否应该显示水位计标签页
      shouldShowWaterLevel() {
        return this.hasWaterLevelDevice
      },
      // 是否应该显示风速风向标签页
      shouldShowAnemometer() {
        return this.hasAnemometerDevice
      },
      // 是否应该显示能见度标签页
      shouldShowVisibility() {
        // 临时方案：桥梁ID为275和276时隐藏能见度计
        if (this.selectedBridgeId === 275 || this.selectedBridgeId === 276) {
          return false
        }
        return this.hasVisibilityDevice
      },
      // 是否应该显示流速计标签页
      shouldShowFlowmeter() {
        // 临时方案：桥梁ID为275和276时隐藏流速计
        if (this.selectedBridgeId === 275 || this.selectedBridgeId === 276) {
          return false
        }
        return this.hasFlowmeterDevice
      },
      // 是否应该显示张力计标签页
      shouldShowTensionmeter() {
        return this.hasTensionmeterDevice
      },
      // 是否应该显示倾角计标签页
      shouldShowInclinometer() {
        return this.hasInclinometerDevice
      },
      // 是否应该显示位移计标签页
      shouldShowDisplacement() {
        return this.hasDisplacementDevice
      },
      // 是否应该显示净高标签页
      shouldShowClearance() {
        return this.hasClearanceDevice
      }
    },
    
    mounted() {
      // 从bridgeStateManager获取当前桥梁ID（参考"桥梁偏航统计"页面的实现方式）
      // 桥梁ID通过localStorage在页面间共享，不需要URL参数
      this.selectedBridgeId = bridgeStateManager.getCurrentBridgeId()
      
      // 临时方案：如果是桥梁ID为275或276，设置默认标签页为张力计，并强制设置张力计设备存在
      if (this.selectedBridgeId === 275 || this.selectedBridgeId === 276) {
        this.activeTab = 'tensionmeter'
        this.hasTensionmeterDevice = true
      }
      
      this.loadBridgeOptions()
      this.updateTime = this.formatTime()
      
      // 监听桥梁状态变化
      this.bridgeChangeUnsubscribe = bridgeStateManager.onBridgeChange((newBridgeId) => {
        if (this.selectedBridgeId !== newBridgeId) {
          this.selectedBridgeId = newBridgeId
          this.onBridgeChange()
        }
      })
      
      // 添加窗口大小变化监听器
      this.resizeHandler = this.debounce(() => {
        this.resizeCharts()
      }, 300)
      window.addEventListener('resize', this.resizeHandler)
      
      // 延迟初始化图表，确保DOM完全渲染
      this.$nextTick(() => {
        setTimeout(() => {
          this.initCharts()
          if (this.selectedBridgeId) {
            this.loadAllData()
          }
        }, 100)
      })
    },
    
    beforeDestroy() {
      this.destroyCharts()
      // 取消桥梁状态变化监听
      if (this.bridgeChangeUnsubscribe) {
        this.bridgeChangeUnsubscribe()
      }
      // 移除窗口大小变化监听器
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
      }
    },
    
    methods: {
      // 获取默认时间范围（最近7天）
      getDefaultTimeRange() {
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - 7)
        
        return [
          this.formatDateTime(start),
          this.formatDateTime(end)
        ]
      },
      
      // 格式化日期时间
      formatDateTime(date) {
        if (!date) {
          return '-'
        }
        
        let dateObj
        if (typeof date === 'string') {
          // 如果是字符串，尝试解析
          dateObj = new Date(date)
          if (isNaN(dateObj.getTime())) {
            return date
          }
        } else if (date instanceof Date) {
          dateObj = date
        } else {
          return '-'
        }
        
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        const hour = String(dateObj.getHours()).padStart(2, '0')
        const minute = String(dateObj.getMinutes()).padStart(2, '0')
        const second = String(dateObj.getSeconds()).padStart(2, '0')
        
        return `${year}-${month}-${day} ${hour}:${minute}:${second}`
      },
      
      // 格式化dataTime数组为日期时间字符串
      formatDataTime(dataTimeArray) {
        if (!dataTimeArray || !Array.isArray(dataTimeArray) || dataTimeArray.length < 5) {
          return '-'
        }
        
        if(dataTimeArray.length === 6) {
        // dataTime格式: [2025, 12, 7, 12, 2, 17] 表示 2025-12-07 12:02:17
          const [year, month, day, hour, minute, second] = dataTimeArray
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
        }
        else if(dataTimeArray.length === 5) {
          // dataTime格式: [2025, 12, 7, 12, 2] 表示 2025-12-07 12:02:00
          const [year, month, day, hour, minute] = dataTimeArray
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00` 
          
        }
      },
      
      // 格式化流速数据
      formatFlowmeterData(item) {
        if (!item) return '-'
        
        // 尝试从不同的字段提取流速值
        const possibleFields = ['flowVelocity', 'flowSpeed', 'speed', 'velocity', 'realTimeValue']
        for (const field of possibleFields) {
          if (item[field] !== undefined && item[field] !== null) {
            const value = parseFloat(item[field])
            return isNaN(value) ? '-' : `${value.toFixed(2)} m/s`
          }
        }
        
        return '-'
      },
      
      // 将dataTime数组转换为Date对象
      dataTimeToDate(dataTimeArray) {
        if (!dataTimeArray || !Array.isArray(dataTimeArray) || dataTimeArray.length < 6) {
          return null
        }
        const [year, month, day, hour, minute, second] = dataTimeArray
        return new Date(year, month - 1, day, hour, minute, second)
      },
      
      // 格式化当前时间
      formatTime() {
        return this.formatDateTime(new Date())
      },
      
      // 加载桥梁选项
      async loadBridgeOptions() {
        this.loading = true
        try {
          const response = await getAllBridges()
          if (response.code === 200 && response.data) {
            // 倒序排列桥梁列表
            this.bridgeOptions = (response.data || [])
              .reverse()
              .map(bridge => ({
                id: bridge.id,
                bridgeName: bridge.bridgeName || bridge.name || `桥梁${bridge.id}`,
                bridgeCode: bridge.bridgeCode,
                latitude: bridge.latitude,
                longitude: bridge.longitude
              }))
            
            // 保存桥梁列表到localStorage
            bridgeStateManager.setBridgeList(this.bridgeOptions)
            
            // 如果没有选中桥梁或选中的桥梁不在列表中，默认选择第一个
            if (!this.selectedBridgeId || !this.bridgeOptions.find(bridge => bridge.id === this.selectedBridgeId)) {
              if (this.bridgeOptions.length > 0) {
                this.selectedBridgeId = this.bridgeOptions[0].id
                bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
              }
            }
          } else {
            this.$message.error(response.message || '加载桥梁列表失败')
          }
        } catch (error) {
          console.error('加载桥梁列表失败:', error)
          this.$message.error('加载桥梁列表失败')
        } finally {
          this.loading = false
        }
      },
      
      // 桥梁变化处理
      onBridgeChange() {
        // 保存桥梁ID到localStorage
        bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
        
        // 重置所有设备标志
        this.resetDeviceFlags()
        
        // 临时方案：桥梁ID为275或276时，强制切换到张力计标签页，并设置张力计设备存在
        if (this.selectedBridgeId === 275 || this.selectedBridgeId === 276) {
          this.activeTab = 'tensionmeter'
          this.hasTensionmeterDevice = true
        }
        
        if (this.selectedBridgeId) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.loadAllData()
            }, 100)
          })
        }
      },
      
      // 重置所有设备存在标志
      resetDeviceFlags() {
        this.hasWaterLevelDevice = false
        this.hasAnemometerDevice = false
        this.hasVisibilityDevice = false
        this.hasFlowmeterDevice = false
        this.hasTensionmeterDevice = false
        this.hasInclinometerDevice = false
        this.hasDisplacementDevice = false
        this.hasClearanceDevice = false
      },
      
      // 时间范围变化处理
      onTimeRangeChange() {
        this.loadAllData()
      },
      
      // 刷新数据
      refreshData() {
        this.loadAllData()
        this.updateTime = this.formatTime()
      },
      
      // 加载所有数据
      async loadAllData() {
        if (!this.selectedBridgeId) {
          this.$message.warning('请先选择桥梁')
          return
        }
        
        if (!this.timeRange || this.timeRange.length !== 2) {
          this.$message.warning('请选择时间范围')
          return
        }
        
        // 先检测所有设备是否存在（并行调用，不显示错误消息）
        await this.detectAllDevices()
        
        // 根据检测结果，如果当前标签页对应的设备不存在，切换到第一个存在的设备标签页
        this.ensureValidActiveTab()
        
        // 等待可见面板渲染完成后初始化其图表实例，再进行数据渲染
        await this.$nextTick()
        this.reinitCurrentTabChart(this.activeTab)
        
        // 加载当前标签页的数据
        await this.loadTabData(this.activeTab)
      },
      
      // 检测所有设备是否存在
      async detectAllDevices() {
        try {
          const response = await getDevicesByBridge(this.selectedBridgeId)
          const deviceTypes = new Set(
            (response.data || []).map(device => (device.device_type || '').toUpperCase())
          )
          this.hasFlowmeterDevice = deviceTypes.has('FLOWMETER')
          this.hasVisibilityDevice = deviceTypes.has('VISIBILITY')
          this.hasWaterLevelDevice = deviceTypes.has('WATER_LEVEL')
          this.hasAnemometerDevice = deviceTypes.has('ANEMOMETER')
          this.hasTensionmeterDevice = deviceTypes.has('TENSIONMETER')
          this.hasInclinometerDevice = deviceTypes.has('INCLINOMETER')
          this.hasDisplacementDevice = deviceTypes.has('DISPLACEMENT')
          this.hasClearanceDevice = deviceTypes.has('WATER_LEVEL')
        } catch (error) {
          console.error('获取桥梁设备列表失败:', error)
        }
      },
      
      // 确保当前激活的标签页是有效的（页面上存在该Tab，且该桥存在对应设备）
      ensureValidActiveTab() {
        // 临时方案：对于桥梁ID为275或276，如果当前是张力计标签页，直接返回不做任何改变
        const isBridge275or276 = this.selectedBridgeId === 275 || this.selectedBridgeId === 276
        if (isBridge275or276 && this.activeTab === 'tensionmeter') {
          return
        }

        // 仅包含页面上实际存在的标签页（按展示顺序，waterLevel 无对应Tab故不参与）
        const uiTabs = [
          { key: 'flowmeter', visible: this.shouldShowFlowmeter },
          { key: 'anemometer', visible: this.shouldShowAnemometer },
          { key: 'visibility', visible: this.shouldShowVisibility },
          { key: 'tensionmeter', visible: this.shouldShowTensionmeter },
          { key: 'inclinometer', visible: this.shouldShowInclinometer },
          { key: 'displacement', visible: this.shouldShowDisplacement },
          { key: 'clearance', visible: this.shouldShowClearance }
        ]

        // 当前激活页有效则保持不变
        if (uiTabs.some(tab => tab.key === this.activeTab && tab.visible)) {
          return
        }

        // 否则切换到第一个既存在Tab又有设备的监测项
        const firstVisible = uiTabs.find(tab => tab.visible)
        if (firstVisible) {
          this.activeTab = firstVisible.key
        }
      },
      
      // 加载标签页数据
      async loadTabData(tabName) {
        if (!this.selectedBridgeId || !this.timeRange || this.timeRange.length !== 2) {
          return
        }

        const pagination = this.pagination[tabName]

        switch (tabName) {
          case 'waterLevel':
            // 水位计：图表使用统计接口，表格使用分页接口
            await Promise.all([
              this.loadWaterLevelStatistics(),
              this.loadWaterLevelData(pagination.pageNum, pagination.pageSize)
            ])
            break
          case 'anemometer':
            // 风速：只使用统计接口（不显示表格）
            await this.loadAnemometerStatistics()
            break
          case 'visibility':
            // 能见度：图表使用统计接口，表格使用分页接口
            await Promise.all([
              this.loadVisibilityStatistics(),
              this.loadVisibilityData(pagination.pageNum, pagination.pageSize)
            ])
            break
          case 'flowmeter':
            // 流速计：图表使用统计接口，表格使用分页接口
            await Promise.all([
              this.loadFlowmeterStatistics(),
              this.loadFlowmeterData(pagination.pageNum, pagination.pageSize)
            ])
            break
          case 'tensionmeter':
            // 张力计：只使用统计接口（不显示表格）
            await this.loadTensionmeterStatistics()
            break
          case 'inclinometer':
            // 倾角计：只使用统计接口
            await this.loadInclinometerStatistics()
            break
          case 'displacement':
            // 位移计：只使用统计接口
            await this.loadDisplacementStatistics()
            break
          case 'clearance':
            // 净高：只使用统计接口
            await this.loadClearanceChartData()
            break
        }
      },
      
      // 加载水位计数据（用于表格）
      async loadWaterLevelData(pageNum = 1, pageSize = 20) {
        this.tableLoading.waterLevel = true

        try {
          const response = await getWaterLevelHistoryData(
            this.selectedBridgeId,
            this.timeRange[0],
            this.timeRange[1],
            pageNum,
            pageSize
          )

          if (response.code === 200 && response.data) {
            this.tableData.waterLevel = response.data.list || []
            this.pagination.waterLevel = {
              pageNum: response.data.pageNum || pageNum,
              pageSize: response.data.pageSize || pageSize,
              total: response.data.total || 0
            }
          } else {
            this.$message.error(response.message || '加载水位计数据失败')
          }
        } catch (error) {
          console.error('加载水位计数据失败:', error)
          this.$message.error('加载水位计数据失败')
        } finally {
          this.tableLoading.waterLevel = false
        }
      },
      
      // 加载能见度计数据（用于表格）
      async loadVisibilityData(pageNum = 1, pageSize = 20) {
        this.tableLoading.visibility = true
        
        try {
          const response = await getVisibilityHistoryData(
            this.selectedBridgeId,
            this.timeRange[0],
            this.timeRange[1],
            pageNum,
            pageSize
          )
          
          if (response.code === 200 && response.data) {
            this.tableData.visibility = response.data.list || []
            this.pagination.visibility = {
              pageNum: response.data.pageNum || pageNum,
              pageSize: response.data.pageSize || pageSize,
              total: response.data.total || 0
            }
          } else {
            this.$message.error(response.message || '加载能见度计数据失败')
          }
        } catch (error) {
          console.error('加载能见度计数据失败:', error)
          this.$message.error('加载能见度计数据失败')
        } finally {
          this.tableLoading.visibility = false
        }
      },
      
      // 加载流速计数据（用于表格）
      async loadFlowmeterData(pageNum = 1, pageSize = 20) {
        this.tableLoading.flowmeter = true
        
        try {
          const response = await getFlowmeterHistoryData(
            this.selectedBridgeId,
            this.timeRange[0],
            this.timeRange[1],
            pageNum,
            pageSize
          )
          
          if (response.code === 200 && response.data) {
            this.tableData.flowmeter = response.data.list || []
            this.pagination.flowmeter = {
              pageNum: response.data.pageNum || pageNum,
              pageSize: response.data.pageSize || pageSize,
              total: response.data.total || 0
            }
          } else {
            this.$message.error(response.message || '加载流速计数据失败')
          }
        } catch (error) {
          console.error('加载流速计数据失败:', error)
          this.$message.error('加载流速计数据失败')
        } finally {
          this.tableLoading.flowmeter = false
        }
      },
      
      // 标签页切换
      handleTabClick(tab) {
        // 延迟加载标签页数据，确保DOM切换完成
        this.$nextTick(() => {
          setTimeout(() => {
            // 先重新初始化图表实例
            this.reinitCurrentTabChart(tab.name)
            // 等待图表实例初始化完成后再加载数据
            setTimeout(() => {
              this.loadTabData(tab.name)
            }, 50)
          }, 150)
        })
      },
      
      // 重新初始化当前标签页的图表（不加载数据）
      reinitCurrentTabChart(tabName) {
        switch (tabName) {
          case 'waterLevel':
            if (this.$refs.waterLevelChart && this.$refs.waterLevelChart.clientWidth > 0) {
              if (this.charts.waterLevel) {
                this.charts.waterLevel.dispose()
              }
              this.charts.waterLevel = echarts.init(this.$refs.waterLevelChart)
            }
            break
          case 'anemometer':
            if (this.$refs.anemometerChart && this.$refs.anemometerChart.clientWidth > 0) {
              if (this.charts.anemometer) {
                this.charts.anemometer.dispose()
              }
              this.charts.anemometer = echarts.init(this.$refs.anemometerChart)
            }
            break
          case 'visibility':
            if (this.$refs.visibilityChart && this.$refs.visibilityChart.clientWidth > 0) {
              if (this.charts.visibility) {
                this.charts.visibility.dispose()
              }
              this.charts.visibility = echarts.init(this.$refs.visibilityChart)
            }
            break
          case 'flowmeter':
            if (this.$refs.flowmeterChart && this.$refs.flowmeterChart.clientWidth > 0) {
              if (this.charts.flowmeter) {
                this.charts.flowmeter.dispose()
              }
              this.charts.flowmeter = echarts.init(this.$refs.flowmeterChart)
            }
            break
          case 'tensionmeter':
            if (this.$refs.tensionmeterChart && this.$refs.tensionmeterChart.clientWidth > 0) {
              if (this.charts.tensionmeter) {
                this.charts.tensionmeter.dispose()
              }
              this.charts.tensionmeter = echarts.init(this.$refs.tensionmeterChart)
            }
            break
          case 'inclinometer':
            if (this.$refs.inclinometerAccChart && this.$refs.inclinometerAccChart.clientWidth > 0) {
              if (this.charts.inclinometerAcc) {
                this.charts.inclinometerAcc.dispose()
              }
              this.charts.inclinometerAcc = echarts.init(this.$refs.inclinometerAccChart)
            }
            if (this.$refs.inclinometerAngleChart && this.$refs.inclinometerAngleChart.clientWidth > 0) {
              if (this.charts.inclinometerAngle) {
                this.charts.inclinometerAngle.dispose()
              }
              this.charts.inclinometerAngle = echarts.init(this.$refs.inclinometerAngleChart)
            }
            break
          case 'displacement':
            if (this.$refs.displacementXChart && this.$refs.displacementXChart.clientWidth > 0) {
              if (this.charts.displacementX) {
                this.charts.displacementX.dispose()
              }
              this.charts.displacementX = echarts.init(this.$refs.displacementXChart)
            }
            if (this.$refs.displacementYChart && this.$refs.displacementYChart.clientWidth > 0) {
              if (this.charts.displacementY) {
                this.charts.displacementY.dispose()
              }
              this.charts.displacementY = echarts.init(this.$refs.displacementYChart)
            }
            if (this.$refs.displacementAbsChart && this.$refs.displacementAbsChart.clientWidth > 0) {
              if (this.charts.displacementAbs) {
                this.charts.displacementAbs.dispose()
              }
              this.charts.displacementAbs = echarts.init(this.$refs.displacementAbsChart)
            }
            break
          case 'clearance':
            if (this.$refs.clearanceChart && this.$refs.clearanceChart.clientWidth > 0) {
              if (this.charts.clearance) {
                this.charts.clearance.dispose()
              }
              this.charts.clearance = echarts.init(this.$refs.clearanceChart)
            }
            break
        }
      },
      
      // 时间粒度变化处理（能见度）
      onVisibilityGranularityChange() {
        // 延迟处理，确保DOM稳定
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadVisibilityStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },
      
      // 时间粒度变化处理（风速）
      onAnemometerGranularityChange() {
        // 延迟处理，确保DOM稳定
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadAnemometerStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },
      
      // 时间粒度变化处理（流速计）
      onFlowmeterGranularityChange() {
        // 延迟处理，确保DOM稳定
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadFlowmeterStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },

      // 时间粒度变化处理（水位计）
      onWaterLevelGranularityChange() {
        // 延迟处理，确保DOM稳定
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadWaterLevelStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },

      // 时间粒度变化处理（张力计）
      onTensionmeterGranularityChange() {
        // 延迟处理，确保DOM稳定
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadTensionmeterStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },

      // 时间粒度变化处理（倾角计）
      onInclinometerGranularityChange() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadInclinometerStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },

      // 时间粒度变化处理（位移计）
      onDisplacementGranularityChange() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadDisplacementStatistics()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },

      // 时间粒度变化处理（净高）
      onClearanceGranularityChange() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadClearanceChartData()
            this.updateTime = this.formatTime()
          }, 100)
        })
      },
      
      // 加载流速计统计聚合数据（用于图表）
      async loadFlowmeterStatistics() {
        if (!this.selectedBridgeId) {
          return
        }
        
        this.chartLoading.flowmeter = true
        
        try {
          const response = await getFlowmeterStatistics(
            this.selectedBridgeId,
            this.flowmeterGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )
          
          // 响应格式：{ code: 200, message: "...", data: {...} }
          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            this.seriesData.flowmeter = series
            this.flowmeterMetrics = this.buildMetricItems(series, item => item.extraData?.avgFlowVelocity, { unit: 'm/s', precision: 2 })
            // 渲染图表
            this.renderFlowmeterChart(series)
          } else {
            this.$message.error(response.message || '加载流速统计数据失败')
          }
        } catch (error) {
          console.error('加载流速统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有流速计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有流速计设备')
          } else {
            this.$message.error('加载流速统计数据失败')
          }
        } finally {
          this.chartLoading.flowmeter = false
        }
      },
      
      // 加载风速统计聚合数据（用于图表和指标卡）
      async loadAnemometerStatistics() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.anemometer = true

        try {
          const response = await getAnemometerStatistics(
            this.selectedBridgeId,
            this.anemometerGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          // 响应格式：{ code: 200, message: "...", data: {...} }
          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            this.seriesData.anemometer = series
            this.anemometerMetrics = this.buildMetricItems(series, item => item.extraData?.avgWindSpeed, { unit: '米/秒', precision: 2 })
            // 渲染图表
            this.renderAnemometerStatisticsChart(series)
          } else {
            this.$message.error(response.message || '加载风速统计数据失败')
          }
        } catch (error) {
          console.error('加载风速统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有风速计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有风速计设备')
          } else {
            this.$message.error('加载风速统计数据失败')
          }
        } finally {
          this.chartLoading.anemometer = false
        }
      },

      // 加载能见度统计聚合数据（用于图表）
      async loadVisibilityStatistics() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.visibility = true

        try {
          const response = await getVisibilityStatistics(
            this.selectedBridgeId,
            this.visibilityGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          // 响应格式：{ code: 200, message: "...", data: {...} }
          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            this.seriesData.visibility = series
            this.visibilityMetrics = this.buildMetricItems(series, item => item.extraData?.avgVisibility, { unit: '米', precision: 1, maxLabel: '最高', minLabel: '最低' })
            // 渲染图表
            this.renderVisibilityChart(series)
          } else {
            this.$message.error(response.message || '加载能见度统计数据失败')
          }
        } catch (error) {
          console.error('加载能见度统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有能见度计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有能见度计设备')
          } else {
            this.$message.error('加载能见度统计数据失败')
          }
        } finally {
          this.chartLoading.visibility = false
        }
      },

      // 加载水位计统计聚合数据（用于图表）
      async loadWaterLevelStatistics() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.waterLevel = true

        try {
          const response = await getWaterLevelStatistics(
            this.selectedBridgeId,
            this.waterLevelGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          // 响应格式：{ code: 200, message: "...", data: {...} }
          if (response.code === 200 && response.data) {
            // 渲染图表
            this.renderWaterLevelChartWithStatistics(response.data.timeSeriesData || [])
          } else {
            this.$message.error(response.message || '加载水位统计数据失败')
          }
        } catch (error) {
          console.error('加载水位统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有水位计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有水位计设备')
          } else {
            this.$message.error('加载水位统计数据失败')
          }
        } finally {
          this.chartLoading.waterLevel = false
        }
      },

      // 加载张力计统计聚合数据（用于图表）
      async loadTensionmeterStatistics() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.tensionmeter = true

        try {
          const response = await getTensionmeterStatistics(
            this.selectedBridgeId,
            this.tensionmeterGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          // 响应格式：{ code: 200, message: "...", data: {...} }
          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            this.seriesData.tensionmeter = series
            this.tensionmeterMetrics = this.buildMetricItems(series, item => item.extraData?.sensor1?.avg, { unit: '', precision: 2 })
            // 渲染图表
            this.renderTensionmeterChart(series)
          } else {
            this.$message.error(response.message || '加载张力计统计数据失败')
          }
        } catch (error) {
          console.error('加载张力计统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有张力计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有张力计设备')
          } else {
            this.$message.error('加载张力计统计数据失败')
          }
        } finally {
          this.chartLoading.tensionmeter = false
        }
      },

      // 加载倾角计统计聚合数据（用于图表）
      async loadInclinometerStatistics() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.inclinometer = true

        try {
          const response = await getInclinometerAccelerationStatistics(
            this.selectedBridgeId,
            this.inclinometerGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            this.seriesData.inclinometer = series
            this.inclinometerMetrics = this.buildMetricItems(series, item => item.extraData?.avgAccX, { unit: 'g', precision: 4 })
            this.renderInclinometerChart(series)
          } else {
            this.$message.error(response.message || '加载倾角计统计数据失败')
          }
        } catch (error) {
          console.error('加载倾角计统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有倾角计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有倾角计设备')
          } else {
            this.$message.error('加载倾角计统计数据失败')
          }
        } finally {
          this.chartLoading.inclinometer = false
        }
      },

      // 加载位移计统计聚合数据（用于图表）
      async loadDisplacementStatistics() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.displacement = true

        try {
          const response = await getDisplacementAbsoluteStatistics(
            this.selectedBridgeId,
            this.displacementGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            this.seriesData.displacement = series
            this.displacementMetrics = this.buildMetricItems(series, item => item.extraData?.avgDisplacementAbs, { unit: 'mm', precision: 2 })
            this.renderDisplacementChart(series)
          } else {
            this.$message.error(response.message || '加载位移计统计数据失败')
          }
        } catch (error) {
          console.error('加载位移计统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有位移计设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有位移计设备')
          } else {
            this.$message.error('加载位移计统计数据失败')
          }
        } finally {
          this.chartLoading.displacement = false
        }
      },
      
      // 加载净高统计聚合数据（用于图表和指标卡）
      async loadClearanceChartData() {
        if (!this.selectedBridgeId) {
          return
        }

        this.chartLoading.clearance = true

        try {
          const response = await getClearanceStatistics(
            this.selectedBridgeId,
            this.clearanceGranularity,
            this.timeRange ? this.timeRange[0] : null,
            this.timeRange ? this.timeRange[1] : null
          )

          if (response.code === 200 && response.data) {
            const series = response.data.timeSeriesData || []
            if (series.length > 0) {
              this.clearanceFallback = false
              this.seriesData.clearance = series
              this.clearanceMetrics = this.buildMetricItems(series, item => item.extraData?.avgClearance, { unit: '米', precision: 2 })
              this.renderClearanceChart(series)
            } else {
              await this.applyClearanceFallback()
            }
          } else {
            this.$message.error(response.message || '加载净高统计数据失败')
          }
        } catch (error) {
          console.error('加载净高统计数据失败:', error)
          // 如果是404错误，说明该桥梁没有净高设备
          if (error.response && error.response.status === 404) {
            console.log('当前桥梁没有净高设备')
          } else {
            this.$message.error('加载净高统计数据失败')
          }
        } finally {
          this.chartLoading.clearance = false
        }
      },

      async applyClearanceFallback() {
        let clearance = null
        try {
          const response = await getWaterLevelRealTimeData(this.selectedBridgeId)
          const latestData = response && response.data && response.data.length > 0 ? response.data[0] : null
          clearance = latestData ? this.extractClearance(latestData.extraProperties) : null
        } catch (error) {
          console.error('加载净高实时校准值失败:', error)
          clearance = null
        }

        if (typeof clearance === 'number' && !isNaN(clearance)) {
          const value = parseFloat(clearance.toFixed(2))
          this.clearanceMetrics = [
            { label: '最新', value, unit: '米', highlight: true },
            { label: '最大', value, unit: '米', highlight: true },
            { label: '最小', value, unit: '米', highlight: true },
            { label: '平均', value, unit: '米' },
            { label: '数据量', value: 0 }
          ]
          const startTime = this.timeRange ? this.timeRange[0] : null
          const endTime = this.timeRange ? this.timeRange[1] : null
          const buildPoint = time => ({
            time,
            timeLabel: time,
            extraData: { avgClearance: value, maxClearance: value, minClearance: value, dataCount: 0 }
          })
          const series = [buildPoint(startTime), buildPoint(endTime)]
          this.seriesData.clearance = series
          this.renderClearanceChart(series)
          this.clearanceFallback = true
        } else {
          this.clearanceFallback = false
          this.seriesData.clearance = []
          this.clearanceMetrics = this.buildMetricItems([], item => item.extraData?.avgClearance, { unit: '米', precision: 2 })
          this.renderClearanceChart([])
        }
      },

      extractClearance(extraProperties) {
        if (!extraProperties) return null

        let clearance = null
        if (extraProperties.emptyHeight !== undefined && extraProperties.emptyHeight !== null) {
          const emptyHeight = parseFloat(extraProperties.emptyHeight)
          clearance = isNaN(emptyHeight) ? null : parseFloat(emptyHeight.toFixed(2))
        }
        let bridgeClearanceHeight = null
        if (extraProperties.bridgeClearanceHeight !== undefined && extraProperties.bridgeClearanceHeight !== null) {
          const bridgeClearanceHeightRaw = parseFloat(extraProperties.bridgeClearanceHeight)
          bridgeClearanceHeight = isNaN(bridgeClearanceHeightRaw) ? null : parseFloat(bridgeClearanceHeightRaw.toFixed(2))
        }
        if (clearance !== null && bridgeClearanceHeight !== null) {
          return clearance - bridgeClearanceHeight
        }
        return null
      },

      // 由统计时间序列构建指标卡数据（最新/最大/最小/平均/数据量）
      buildMetricItems(points, valueGetter, options = {}) {
        const {
          unit = '',
          precision = 2,
          latestLabel = '最新',
          maxLabel = '最大',
          minLabel = '最小'
        } = options

        const list = Array.isArray(points) ? points : []
        const sortedData = [...list].sort((a, b) => {
          const dateA = a && a.time ? new Date(a.time) : null
          const dateB = b && b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })

        const values = sortedData
          .map(item => valueGetter(item))
          .filter(value => typeof value === 'number' && !isNaN(value))

        const formatValue = value => {
          if (typeof value !== 'number' || isNaN(value)) return '-'
          return parseFloat(value.toFixed(precision))
        }

        if (values.length === 0) {
          return [
            { label: latestLabel, value: '-' },
            { label: maxLabel, value: '-' },
            { label: minLabel, value: '-' },
            { label: '平均', value: '-' },
            { label: '数据量', value: this.calcDataCount(sortedData) }
          ]
        }

        const latest = values[values.length - 1]
        const max = Math.max(...values)
        const min = Math.min(...values)
        const avg = values.reduce((sum, value) => sum + value, 0) / values.length

        return [
          { label: latestLabel, value: formatValue(latest), unit, highlight: true },
          { label: maxLabel, value: formatValue(max), unit, highlight: true },
          { label: minLabel, value: formatValue(min), unit, highlight: true },
          { label: '平均', value: formatValue(avg), unit },
          { label: '数据量', value: this.calcDataCount(sortedData) }
        ]
      },

      // 统计时间序列的数据量（优先累加 dataCount，否则使用数据点数量）
      calcDataCount(points) {
        if (!Array.isArray(points) || points.length === 0) return 0
        let total = 0
        let hasCount = false
        points.forEach(point => {
          const count = point && point.extraData ? point.extraData.dataCount : undefined
          if (typeof count === 'number' && !isNaN(count)) {
            total += count
            hasCount = true
          }
        })
        return hasCount ? total : points.length
      },
      
      /**
       * 导出当前图表数据为 CSV（带 BOM，Excel 直接打开不乱码）
       * 数据直接取自 echarts 实例的 option，保证与页面所见一致
       */
      exportChartCsv(tabName, fileTitle) {
        try {
          const chart = this.charts && this.charts[tabName]
          if (!chart) {
            this.$message.warning('暂无图表数据可导出')
            return
          }
          const option = chart.getOption() || {}
          const xAxis = (option.xAxis && option.xAxis[0] && option.xAxis[0].data) || []
          const series = option.series || []
          if (!xAxis.length || !series.length) {
            this.$message.warning('暂无图表数据可导出')
            return
          }
          const headers = ['时间'].concat(series.map(s => s.name || '数值'))
          const rows = xAxis.map((x, i) => {
            const cells = series.map(s => {
              const v = s.data ? s.data[i] : ''
              return v === null || v === undefined ? '' : v
            })
            return [x].concat(cells)
          })
          const escape = (val) => {
            const str = val === null || val === undefined ? '' : String(val)
            return /[",\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str
          }
          const csv = [headers].concat(rows).map(r => r.map(escape).join(',')).join('\r\n')
          const matched = (this.bridgeOptions || []).find(b => b.id === this.selectedBridgeId)
          const bridgeName = (matched && matched.name) ? matched.name : ('桥梁' + (this.selectedBridgeId || ''))
          const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')
          const fileName = `${bridgeName}_${fileTitle || tabName}_${stamp}.csv`
          const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          this.$message.success(`已导出 ${rows.length} 条数据`)
        } catch (e) {
          console.error('导出 CSV 失败:', e)
          this.$message.error('导出失败，请稍后重试')
        }
      },

      // 刷新图表数据
      refreshChartData(tabName) {
        // 延迟刷新，确保DOM稳定
        this.$nextTick(() => {
          setTimeout(() => {
            if (tabName === 'visibility') {
              // 能见度：只刷新图表数据（统计接口）
              this.loadVisibilityStatistics()
            } else if (tabName === 'anemometer') {
              // 风速：只刷新图表数据（统计接口）
              this.loadAnemometerStatistics()
            } else if (tabName === 'flowmeter') {
              // 流速计：只刷新图表数据（统计接口）
              this.loadFlowmeterStatistics()
            } else if (tabName === 'waterLevel') {
              // 水位计：只刷新图表数据（统计接口）
              this.loadWaterLevelStatistics()
            } else if (tabName === 'tensionmeter') {
              // 张力计：只刷新图表数据（统计接口）
              this.loadTensionmeterStatistics()
            } else if (tabName === 'inclinometer') {
              // 倾角计：只刷新图表数据（统计接口）
              this.loadInclinometerStatistics()
            } else if (tabName === 'displacement') {
              // 位移计：只刷新图表数据（统计接口）
              this.loadDisplacementStatistics()
            } else if (tabName === 'clearance') {
              // 净高：只刷新图表数据（统计接口）
              this.loadClearanceChartData()
            } else {
              // 其他设备：刷新整个标签页数据
              this.loadTabData(tabName)
            }
            this.updateTime = this.formatTime()
          }, 100)
        })
      },
      
      // 初始化图表
      initCharts() {
        this.$nextTick(() => {
          // 确保DOM元素存在且有尺寸
          if (this.$refs.waterLevelChart && this.$refs.waterLevelChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.waterLevel) {
              this.charts.waterLevel.dispose()
            }
            this.charts.waterLevel = echarts.init(this.$refs.waterLevelChart)
          }
          if (this.$refs.anemometerChart && this.$refs.anemometerChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.anemometer) {
              this.charts.anemometer.dispose()
            }
            this.charts.anemometer = echarts.init(this.$refs.anemometerChart)
          }
          if (this.$refs.visibilityChart && this.$refs.visibilityChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.visibility) {
              this.charts.visibility.dispose()
            }
            this.charts.visibility = echarts.init(this.$refs.visibilityChart)
          }
          if (this.$refs.flowmeterChart && this.$refs.flowmeterChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.flowmeter) {
              this.charts.flowmeter.dispose()
            }
            this.charts.flowmeter = echarts.init(this.$refs.flowmeterChart)
          }
          if (this.$refs.tensionmeterChart && this.$refs.tensionmeterChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.tensionmeter) {
              this.charts.tensionmeter.dispose()
            }
            this.charts.tensionmeter = echarts.init(this.$refs.tensionmeterChart)
          }
          if (this.$refs.inclinometerAccChart && this.$refs.inclinometerAccChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.inclinometerAcc) {
              this.charts.inclinometerAcc.dispose()
            }
            this.charts.inclinometerAcc = echarts.init(this.$refs.inclinometerAccChart)
          }
          if (this.$refs.inclinometerAngleChart && this.$refs.inclinometerAngleChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.inclinometerAngle) {
              this.charts.inclinometerAngle.dispose()
            }
            this.charts.inclinometerAngle = echarts.init(this.$refs.inclinometerAngleChart)
          }
          if (this.$refs.displacementXChart && this.$refs.displacementXChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.displacementX) {
              this.charts.displacementX.dispose()
            }
            this.charts.displacementX = echarts.init(this.$refs.displacementXChart)
          }
          if (this.$refs.displacementYChart && this.$refs.displacementYChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.displacementY) {
              this.charts.displacementY.dispose()
            }
            this.charts.displacementY = echarts.init(this.$refs.displacementYChart)
          }
          if (this.$refs.displacementAbsChart && this.$refs.displacementAbsChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.displacementAbs) {
              this.charts.displacementAbs.dispose()
            }
            this.charts.displacementAbs = echarts.init(this.$refs.displacementAbsChart)
          }
          if (this.$refs.clearanceChart && this.$refs.clearanceChart.clientWidth > 0) {
            // 如果已存在实例，先销毁
            if (this.charts.clearance) {
              this.charts.clearance.dispose()
            }
            this.charts.clearance = echarts.init(this.$refs.clearanceChart)
          }
        })
      },
      
      // 确保指定图表实例存在且与当前 DOM 元素绑定
      ensureChartInstance(key, refName) {
        const el = this.$refs[refName]
        if (!el) return null
        let inst = this.charts[key]
        if (inst && inst.getDom && inst.getDom() !== el) {
          inst.dispose()
          inst = null
        }
        if (!inst) {
          inst = echarts.getInstanceByDom(el) || echarts.init(el)
          this.charts[key] = inst
        }
        return inst
      },
      
      // 渲染水位计图表（使用统计聚合数据）
      renderWaterLevelChartWithStatistics(data) {
        if (!this.ensureChartInstance('waterLevel', 'waterLevelChart')) return

        if (!data || data.length === 0) {
          this.charts.waterLevel.setOption({})
          return
        }

        // 按时间排序（统计接口返回的数据格式）
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.time ? new Date(a.time) : null
          const dateB = b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })

        // 格式化时间标签
        const times = sortedData.map(item => {
          if (item.timeLabel) {
            // 如果已有timeLabel，根据粒度简化显示
            if (this.waterLevelGranularity === 'day') {
              // 按天显示：提取日期部分 MM-DD
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]}`
              }
            } else {
              // 按小时显示：提取日期和时间 MM-DD HH:mm
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
              }
            }
            return item.timeLabel
          }
          if (item.time) {
            const date = new Date(item.time)
            if (this.waterLevelGranularity === 'day') {
              // 按天显示：MM-DD
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            } else {
              // 按小时显示：MM-DD HH:mm
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
            }
          }
          return '-'
        })

        // 提取平均值
        const avgValues = sortedData.map(item => {
          const value = item.extraData?.avgWaterLevel || item.value
          return typeof value === 'number' ? parseFloat(value.toFixed(2)) : null
        })

        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                result += param.marker + (param.seriesName || '水位') + ': ' + param.value + '米<br/>'
              })
              return result
            }
          },
          legend: {
            show: false
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: times,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            type: 'value',
            name: '水位(米)'
          },
          series: [
            {
              name: '',
              type: 'line',
              smooth: true,
              data: avgValues,
              itemStyle: {
                color: '#3b82f6'
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
                    { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }
                  ]
                }
              }
            }
          ]
        }

        this.charts.waterLevel.setOption(option)
      },
      
      // 渲染风速统计图表（使用统计聚合数据 - 平均值 + 最大/最小值）
      renderAnemometerStatisticsChart(data) {
        if (!this.ensureChartInstance('anemometer', 'anemometerChart')) return

        if (!data || data.length === 0) {
          this.charts.anemometer.setOption({})
          return
        }

        // 按时间排序（统计接口返回的数据格式）
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.time ? new Date(a.time) : null
          const dateB = b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })

        // 格式化时间标签
        const times = sortedData.map(item => {
          if (item.timeLabel) {
            if (this.anemometerGranularity === 'day') {
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]}`
              }
            } else {
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
              }
            }
            return item.timeLabel
          }
          if (item.time) {
            const date = new Date(item.time)
            if (this.anemometerGranularity === 'day') {
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            } else {
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
            }
          }
          return '-'
        })

        const toFixedOrNull = (value, digits) => (typeof value === 'number' ? parseFloat(value.toFixed(digits)) : null)

        const avgValues = sortedData.map(item => toFixedOrNull(item.extraData?.avgWindSpeed, 2))
        const maxValues = sortedData.map(item => toFixedOrNull(item.extraData?.maxWindSpeed, 2))
        const minValues = sortedData.map(item => toFixedOrNull(item.extraData?.minWindSpeed, 2))

        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                if (param.value !== null && param.value !== undefined) {
                  result += param.marker + param.seriesName + ': ' + param.value + '米/秒<br/>'
                }
              })
              return result
            }
          },
          legend: {
            data: ['平均风速', '最大风速', '最小风速'],
            top: '0%'
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '12%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: times,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            type: 'value',
            name: '风速(米/秒)'
          },
          series: [
            {
              name: '平均风速',
              type: 'line',
              smooth: true,
              data: avgValues,
              itemStyle: {
                color: '#0ea5e9'
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(14, 165, 233, 0.3)' },
                    { offset: 1, color: 'rgba(14, 165, 233, 0.1)' }
                  ]
                }
              }
            },
            {
              name: '最大风速',
              type: 'line',
              smooth: true,
              data: maxValues,
              itemStyle: {
                color: '#ef4444'
              },
              lineStyle: {
                width: 1.5,
                type: 'dashed'
              }
            },
            {
              name: '最小风速',
              type: 'line',
              smooth: true,
              data: minValues,
              itemStyle: {
                color: '#10b981'
              },
              lineStyle: {
                width: 1.5,
                type: 'dashed'
              }
            }
          ]
        }

        this.charts.anemometer.setOption(option)
      },
      
      // 渲染能见度计图表（使用统计聚合数据）
      renderVisibilityChart(data) {
        if (!this.ensureChartInstance('visibility', 'visibilityChart')) return
        
        if (!data || data.length === 0) {
          this.charts.visibility.setOption({})
          return
        }
        
        // 按时间排序（统计接口返回的数据格式）
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.time ? new Date(a.time) : null
          const dateB = b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })
        
        // 格式化时间标签
        const times = sortedData.map(item => {
          if (item.timeLabel) {
            // 如果已有timeLabel，根据粒度简化显示
            if (this.visibilityGranularity === 'day') {
              // 按天显示：提取日期部分 MM-DD
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]}`
              }
            } else {
              // 按小时显示：提取日期和时间 MM-DD HH:mm
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
              }
            }
            return item.timeLabel
          }
          if (item.time) {
            const date = new Date(item.time)
            if (this.visibilityGranularity === 'day') {
              // 按天显示：MM-DD
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            } else {
              // 按小时显示：MM-DD HH:mm
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
            }
          }
          return '-'
        })
        
        // 提取平均值
        const avgValues = sortedData.map(item => {
          const value = item.extraData?.avgVisibility || item.value
          return typeof value === 'number' ? parseFloat(value.toFixed(1)) : null
        })
        
        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                result += param.marker + (param.seriesName || '能见度') + ': ' + param.value + '米<br/>'
              })
              return result
            }
          },
          legend: {
            show: false
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: times,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            type: 'value',
            name: '能见度(米)'
          },
          series: [
            {
              name: '',
              type: 'line',
              smooth: true,
              data: avgValues,
              itemStyle: {
                color: '#8b5cf6'
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(139, 92, 246, 0.3)' },
                    { offset: 1, color: 'rgba(139, 92, 246, 0.1)' }
                  ]
                }
              }
            }
          ]
        }
        
        this.charts.visibility.setOption(option)
      },
      
      // 渲染流速计图表（使用统计聚合数据）
      renderFlowmeterChart(data) {
        if (!this.ensureChartInstance('flowmeter', 'flowmeterChart')) return
        
        if (!data || data.length === 0) {
          this.charts.flowmeter.setOption({})
          return
        }
        
        // 按时间排序（统计接口返回的数据格式）
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.time ? new Date(a.time) : null
          const dateB = b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })
        
        // 格式化时间标签
        const times = sortedData.map(item => {
          if (item.timeLabel) {
            // 如果已有timeLabel，根据粒度简化显示
            if (this.flowmeterGranularity === 'day') {
              // 按天显示：提取日期部分 MM-DD
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]}`
              }
            } else {
              // 按小时显示：提取日期和时间 MM-DD HH:mm
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
              }
            }
            return item.timeLabel
          }
          if (item.time) {
            const date = new Date(item.time)
            if (this.flowmeterGranularity === 'day') {
              // 按天显示：MM-DD
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            } else {
              // 按小时显示：MM-DD HH:mm
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
            }
          }
          return '-'
        })
        
        // 提取平均值
        const avgValues = sortedData.map(item => {
          const value = item.extraData?.avgFlowVelocity || item.value
          return typeof value === 'number' ? parseFloat(value.toFixed(2)) : null
        })
        
        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                result += param.marker + (param.seriesName || '流速') + ': ' + param.value + 'm/s<br/>'
              })
              return result
            }
          },
          legend: {
            show: false
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: times,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            type: 'value',
            name: '流速(m/s)'
          },
          series: [
            {
              name: '',
              type: 'line',
              smooth: true,
              data: avgValues,
              itemStyle: {
                color: '#06b6d4'
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(6, 182, 212, 0.3)' },
                    { offset: 1, color: 'rgba(6, 182, 212, 0.1)' }
                  ]
                }
              }
            }
          ]
        }
        
        this.charts.flowmeter.setOption(option)
      },

      // 渲染张力计图表（使用统计聚合数据 - 多传感器版本）
      renderTensionmeterChart(data) {
        if (!this.ensureChartInstance('tensionmeter', 'tensionmeterChart')) return
        
        if (!data || data.length === 0) {
          this.charts.tensionmeter.setOption({})
          return
        }
        
        // 按时间排序（统计接口返回的数据格式）
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.time ? new Date(a.time) : null
          const dateB = b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })
        
        // 格式化时间标签
        const times = sortedData.map(item => {
          if (item.timeLabel) {
            // 如果已有timeLabel，根据粒度简化显示
            if (this.tensionmeterGranularity === 'day') {
              // 按天显示：提取日期部分 MM-DD
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]}`
              }
            } else {
              // 按小时显示：提取日期和时间 MM-DD HH:mm
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
              }
            }
            return item.timeLabel
          }
          if (item.time) {
            const date = new Date(item.time)
            if (this.tensionmeterGranularity === 'day') {
              // 按天显示：MM-DD
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            } else {
              // 按小时显示：MM-DD HH:mm
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
            }
          }
          return '-'
        })
        
        // 提取3个传感器的平均值
        const sensor1Avg = sortedData.map(item => {
          const value = item.extraData?.sensor1?.avg
          return typeof value === 'number' ? parseFloat(value.toFixed(2)) : null
        })
        
        const sensor2Avg = sortedData.map(item => {
          const value = item.extraData?.sensor2?.avg
          return typeof value === 'number' ? parseFloat(value.toFixed(2)) : null
        })
        
        const sensor3Avg = sortedData.map(item => {
          const value = item.extraData?.sensor3?.avg
          return typeof value === 'number' ? parseFloat(value.toFixed(2)) : null
        })
        
        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                if (param.value !== null && param.value !== undefined) {
                  result += param.marker + param.seriesName + ': ' + param.value + '<br/>'
                }
              })
              return result
            }
          },
          legend: {
            data: ['传感器1', '传感器2', '传感器3'],
            top: '0%'
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '12%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: times,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            type: 'value',
            name: '张力值'
          },
          series: [
            {
              name: '传感器1',
              type: 'line',
              smooth: true,
              data: sensor1Avg,
              itemStyle: {
                color: '#f59e0b'
              },
              lineStyle: {
                width: 2
              }
            },
            {
              name: '传感器2',
              type: 'line',
              smooth: true,
              data: sensor2Avg,
              itemStyle: {
                color: '#3b82f6'
              },
              lineStyle: {
                width: 2
              }
            },
            {
              name: '传感器3',
              type: 'line',
              smooth: true,
              data: sensor3Avg,
              itemStyle: {
                color: '#10b981'
              },
              lineStyle: {
                width: 2
              }
            }
          ]
        }
        
        this.charts.tensionmeter.setOption(option)
      },

      // 渲染倾角计图表（多设备多维度 - 分为加速度和角度两个图表）
      renderInclinometerChart(data) {
        this.ensureChartInstance('inclinometerAcc', 'inclinometerAccChart')
        this.ensureChartInstance('inclinometerAngle', 'inclinometerAngleChart')

        if (!data || data.length === 0) {
          if (this.charts.inclinometerAcc) this.charts.inclinometerAcc.setOption({})
          if (this.charts.inclinometerAngle) this.charts.inclinometerAngle.setOption({})
          return
        }
        
        // 按设备分组
        const deviceGroups = {}
        data.forEach(item => {
          const deviceCode = item.extraData?.vendorDeviceCode || '未知设备'
          if (!deviceGroups[deviceCode]) {
            deviceGroups[deviceCode] = []
          }
          deviceGroups[deviceCode].push(item)
        })
        
        // 提取时间轴（去重并排序）
        const timeSet = new Set()
        data.forEach(item => {
          if (item.timeLabel) {
            timeSet.add(item.timeLabel)
          }
        })
        const timeLabels = Array.from(timeSet).sort()
        
        // 格式化时间标签
        const times = timeLabels.map(timeLabel => {
          if (this.inclinometerGranularity === 'day') {
            const dateMatch = timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              return `${dateMatch[2]}-${dateMatch[3]}`
            }
          } else {
            const dateMatch = timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
            if (dateMatch) {
              return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
            }
          }
          return timeLabel
        })
        
        // 颜色方案
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']
        
        // ========== 渲染加速度图表（双Y轴优化） ==========
        if (this.charts.inclinometerAcc && this.$refs.inclinometerAccChart) {
          const accSeries = []
          
          Object.keys(deviceGroups).forEach((deviceCode, deviceIndex) => {
            const deviceData = deviceGroups[deviceCode]
            const baseColor = colors[deviceIndex % colors.length]
            
            // X轴加速度
            const accXValues = timeLabels.map(time => {
              const point = deviceData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgAccX !== undefined) {
                return parseFloat(point.extraData.avgAccX.toFixed(4))
              }
              return null
            })
            
            // Y轴加速度
            const accYValues = timeLabels.map(time => {
              const point = deviceData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgAccY !== undefined) {
                return parseFloat(point.extraData.avgAccY.toFixed(4))
              }
              return null
            })
            
            // Z轴加速度
            const accZValues = timeLabels.map(time => {
              const point = deviceData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgAccZ !== undefined) {
                return parseFloat(point.extraData.avgAccZ.toFixed(4))
              }
              return null
            })
            
            // X、Y轴使用左Y轴（通常是小数值）
            accSeries.push(
              {
                name: `${deviceCode} - X轴 (左)`,
                type: 'line',
                smooth: true,
                yAxisIndex: 0, // 绑定到左Y轴
                data: accXValues,
                itemStyle: { color: baseColor },
                lineStyle: { width: 2, type: 'solid' }
              },
              {
                name: `${deviceCode} - Y轴 (左)`,
                type: 'line',
                smooth: true,
                yAxisIndex: 0, // 绑定到左Y轴
                data: accYValues,
                itemStyle: { color: baseColor },
                lineStyle: { width: 2, type: 'dashed' }
              }
            )
            
            // Z轴使用右Y轴（通常接近1g的重力加速度）
            accSeries.push({
              name: `${deviceCode} - Z轴 (右)`,
              type: 'line',
              smooth: true,
              yAxisIndex: 1, // 绑定到右Y轴
              data: accZValues,
              itemStyle: { color: baseColor },
              lineStyle: { width: 2, type: 'dotted' }
            })
          })
          
          const accOption = {
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'cross' },
              formatter: function(params) {
                let result = params[0].name + '<br/>'
                params.forEach(param => {
                  if (param.value !== null && param.value !== undefined) {
                    result += param.marker + param.seriesName + ': ' + param.value + ' g<br/>'
                  }
                })
                return result
              }
            },
            legend: {
              data: accSeries.map(s => s.name),
              top: '0%',
              type: 'scroll'
            },
            grid: {
              left: '3%',
              right: '8%',
              bottom: '3%',
              top: '15%',
              containLabel: true
            },
            xAxis: {
              type: 'category',
              data: times,
              axisTick: { alignWithLabel: true }
            },
            yAxis: [
              {
                type: 'value',
                name: 'X/Y轴加速度 (g)',
                position: 'left',
                axisLabel: {
                  formatter: '{value}'
                },
                axisLine: {
                  lineStyle: {
                    color: '#666'
                  }
                }
              },
              {
                type: 'value',
                name: 'Z轴加速度 (g)',
                position: 'right',
                axisLabel: {
                  formatter: '{value}'
                },
                axisLine: {
                  lineStyle: {
                    color: '#666'
                  }
                }
              }
            ],
            series: accSeries
          }
          
          this.charts.inclinometerAcc.setOption(accOption)
        }
        
        // ========== 渲染角度图表（智能双Y轴优化） ==========
        if (this.charts.inclinometerAngle && this.$refs.inclinometerAngleChart) {
          const angleSeries = []
          let allAngleValues = [] // 用于分析数值范围
          
          Object.keys(deviceGroups).forEach((deviceCode, deviceIndex) => {
            const deviceData = deviceGroups[deviceCode]
            const baseColor = colors[deviceIndex % colors.length]
            
            // X轴角度
            const angleXValues = timeLabels.map(time => {
              const point = deviceData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgAngleX !== undefined) {
                return parseFloat(point.extraData.avgAngleX.toFixed(2))
              }
              return null
            })
            
            // Y轴角度
            const angleYValues = timeLabels.map(time => {
              const point = deviceData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgAngleY !== undefined) {
                return parseFloat(point.extraData.avgAngleY.toFixed(2))
              }
              return null
            })
            
            // Z轴角度
            const angleZValues = timeLabels.map(time => {
              const point = deviceData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgAngleZ !== undefined) {
                return parseFloat(point.extraData.avgAngleZ.toFixed(2))
              }
              return null
            })
            
            // 收集所有有效数值用于范围分析
            allAngleValues.push(
              ...angleXValues.filter(v => v !== null),
              ...angleYValues.filter(v => v !== null),
              ...angleZValues.filter(v => v !== null)
            )
            
            // 计算各轴的数值范围
            const xRange = this.calculateRange(angleXValues.filter(v => v !== null))
            const yRange = this.calculateRange(angleYValues.filter(v => v !== null))
            const zRange = this.calculateRange(angleZValues.filter(v => v !== null))
            
            // 判断是否需要双Y轴：如果Z轴范围与X/Y轴范围差异超过5倍，则使用双Y轴
            const xyAvgRange = (xRange + yRange) / 2
            const needDualAxis = zRange > 0 && xyAvgRange > 0 && (zRange / xyAvgRange > 5 || xyAvgRange / zRange > 5)
            
            if (needDualAxis) {
              // 使用双Y轴
              angleSeries.push(
                {
                  name: `${deviceCode} - X轴 (左)`,
                  type: 'line',
                  smooth: true,
                  yAxisIndex: 0,
                  data: angleXValues,
                  itemStyle: { color: baseColor },
                  lineStyle: { width: 2, type: 'solid' }
                },
                {
                  name: `${deviceCode} - Y轴 (左)`,
                  type: 'line',
                  smooth: true,
                  yAxisIndex: 0,
                  data: angleYValues,
                  itemStyle: { color: baseColor },
                  lineStyle: { width: 2, type: 'dashed' }
                },
                {
                  name: `${deviceCode} - Z轴 (右)`,
                  type: 'line',
                  smooth: true,
                  yAxisIndex: 1,
                  data: angleZValues,
                  itemStyle: { color: baseColor },
                  lineStyle: { width: 2, type: 'dotted' }
                }
              )
            } else {
              // 使用单Y轴
              angleSeries.push(
                {
                  name: `${deviceCode} - X轴`,
                  type: 'line',
                  smooth: true,
                  data: angleXValues,
                  itemStyle: { color: baseColor },
                  lineStyle: { width: 2, type: 'solid' }
                },
                {
                  name: `${deviceCode} - Y轴`,
                  type: 'line',
                  smooth: true,
                  data: angleYValues,
                  itemStyle: { color: baseColor },
                  lineStyle: { width: 2, type: 'dashed' }
                },
                {
                  name: `${deviceCode} - Z轴`,
                  type: 'line',
                  smooth: true,
                  data: angleZValues,
                  itemStyle: { color: baseColor },
                  lineStyle: { width: 2, type: 'dotted' }
                }
              )
            }
          })
          
          // 判断是否使用了双Y轴
          const useDualAxis = angleSeries.some(s => s.yAxisIndex === 1)
          
          const angleOption = {
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'cross' },
              formatter: function(params) {
                let result = params[0].name + '<br/>'
                params.forEach(param => {
                  if (param.value !== null && param.value !== undefined) {
                    result += param.marker + param.seriesName + ': ' + param.value + '°<br/>'
                  }
                })
                return result
              }
            },
            legend: {
              data: angleSeries.map(s => s.name),
              top: '0%',
              type: 'scroll'
            },
            grid: {
              left: '3%',
              right: useDualAxis ? '8%' : '4%',
              bottom: '3%',
              top: '15%',
              containLabel: true
            },
            xAxis: {
              type: 'category',
              data: times,
              axisTick: { alignWithLabel: true }
            },
            yAxis: useDualAxis ? [
              {
                type: 'value',
                name: 'X/Y轴角度 (°)',
                position: 'left',
                axisLabel: {
                  formatter: '{value}'
                },
                axisLine: {
                  lineStyle: {
                    color: '#666'
                  }
                }
              },
              {
                type: 'value',
                name: 'Z轴角度 (°)',
                position: 'right',
                axisLabel: {
                  formatter: '{value}'
                },
                axisLine: {
                  lineStyle: {
                    color: '#666'
                  }
                }
              }
            ] : {
              type: 'value',
              name: '角度 (°)',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: angleSeries
          }
          
          this.charts.inclinometerAngle.setOption(angleOption)
        }
      },

      // 渲染位移计图表（多测点多维度 - 分为X、Y、绝对位移三个图表）
      renderDisplacementChart(data) {
        this.ensureChartInstance('displacementX', 'displacementXChart')
        this.ensureChartInstance('displacementY', 'displacementYChart')
        this.ensureChartInstance('displacementAbs', 'displacementAbsChart')

        if (!data || data.length === 0) {
          if (this.charts.displacementX) this.charts.displacementX.setOption({})
          if (this.charts.displacementY) this.charts.displacementY.setOption({})
          if (this.charts.displacementAbs) this.charts.displacementAbs.setOption({})
          return
        }
        
        // 按测点分组
        const pointGroups = {}
        data.forEach(item => {
          const pointName = item.extraData?.pointName || `测点${item.extraData?.pointId || ''}`
          if (!pointGroups[pointName]) {
            pointGroups[pointName] = []
          }
          pointGroups[pointName].push(item)
        })
        
        // 提取时间轴（去重并排序）
        const timeSet = new Set()
        data.forEach(item => {
          if (item.timeLabel) {
            timeSet.add(item.timeLabel)
          }
        })
        const timeLabels = Array.from(timeSet).sort()
        
        // 格式化时间标签
        const times = timeLabels.map(timeLabel => {
          if (this.displacementGranularity === 'day') {
            const dateMatch = timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              return `${dateMatch[2]}-${dateMatch[3]}`
            }
          } else {
            const dateMatch = timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
            if (dateMatch) {
              return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
            }
          }
          return timeLabel
        })
        
        // 颜色方案
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']
        
        // ========== 渲染X轴位移图表 ==========
        if (this.charts.displacementX && this.$refs.displacementXChart) {
          const xSeries = Object.keys(pointGroups).map((pointName, index) => {
            const pointData = pointGroups[pointName]
            
            const values = timeLabels.map(time => {
              const point = pointData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgDisplacementX !== undefined) {
                return parseFloat(point.extraData.avgDisplacementX.toFixed(2))
              }
              return null
            })
            
            return {
              name: pointName,
              type: 'line',
              smooth: true,
              data: values,
              itemStyle: { color: colors[index % colors.length] },
              lineStyle: { width: 2 }
            }
          })
          
          const xOption = {
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'cross' },
              formatter: function(params) {
                let result = params[0].name + '<br/>'
                params.forEach(param => {
                  if (param.value !== null && param.value !== undefined) {
                    result += param.marker + param.seriesName + ': ' + param.value + ' mm<br/>'
                  }
                })
                return result
              }
            },
            legend: {
              data: Object.keys(pointGroups),
              top: '0%',
              type: 'scroll'
            },
            grid: {
              left: '3%',
              right: '4%',
              bottom: '3%',
              top: '12%',
              containLabel: true
            },
            xAxis: {
              type: 'category',
              data: times,
              axisTick: { alignWithLabel: true }
            },
            yAxis: {
              type: 'value',
              name: 'X轴位移 (mm)',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: xSeries
          }
          
          this.charts.displacementX.setOption(xOption)
        }
        
        // ========== 渲染Y轴位移图表 ==========
        if (this.charts.displacementY && this.$refs.displacementYChart) {
          const ySeries = Object.keys(pointGroups).map((pointName, index) => {
            const pointData = pointGroups[pointName]
            
            const values = timeLabels.map(time => {
              const point = pointData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgDisplacementY !== undefined) {
                return parseFloat(point.extraData.avgDisplacementY.toFixed(2))
              }
              return null
            })
            
            return {
              name: pointName,
              type: 'line',
              smooth: true,
              data: values,
              itemStyle: { color: colors[index % colors.length] },
              lineStyle: { width: 2 }
            }
          })
          
          const yOption = {
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'cross' },
              formatter: function(params) {
                let result = params[0].name + '<br/>'
                params.forEach(param => {
                  if (param.value !== null && param.value !== undefined) {
                    result += param.marker + param.seriesName + ': ' + param.value + ' mm<br/>'
                  }
                })
                return result
              }
            },
            legend: {
              data: Object.keys(pointGroups),
              top: '0%',
              type: 'scroll'
            },
            grid: {
              left: '3%',
              right: '4%',
              bottom: '3%',
              top: '12%',
              containLabel: true
            },
            xAxis: {
              type: 'category',
              data: times,
              axisTick: { alignWithLabel: true }
            },
            yAxis: {
              type: 'value',
              name: 'Y轴位移 (mm)',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: ySeries
          }
          
          this.charts.displacementY.setOption(yOption)
        }
        
        // ========== 渲染绝对位移图表 ==========
        if (this.charts.displacementAbs && this.$refs.displacementAbsChart) {
          const absSeries = Object.keys(pointGroups).map((pointName, index) => {
            const pointData = pointGroups[pointName]
            
            const values = timeLabels.map(time => {
              const point = pointData.find(p => p.timeLabel === time)
              if (point && point.extraData?.avgDisplacementAbs !== undefined) {
                return parseFloat(point.extraData.avgDisplacementAbs.toFixed(2))
              }
              return null
            })
            
            return {
              name: pointName,
              type: 'line',
              smooth: true,
              data: values,
              itemStyle: { color: colors[index % colors.length] },
              lineStyle: { width: 2 },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: colors[index % colors.length] + '40' },
                    { offset: 1, color: colors[index % colors.length] + '10' }
                  ]
                }
              }
            }
          })
          
          const absOption = {
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'cross' },
              formatter: function(params) {
                let result = params[0].name + '<br/>'
                params.forEach(param => {
                  if (param.value !== null && param.value !== undefined) {
                    result += param.marker + param.seriesName + ': ' + param.value + ' mm<br/>'
                  }
                })
                return result
              }
            },
            legend: {
              data: Object.keys(pointGroups),
              top: '0%',
              type: 'scroll'
            },
            grid: {
              left: '3%',
              right: '4%',
              bottom: '3%',
              top: '12%',
              containLabel: true
            },
            xAxis: {
              type: 'category',
              data: times,
              axisTick: { alignWithLabel: true }
            },
            yAxis: {
              type: 'value',
              name: '绝对位移 (mm)',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: absSeries
          }
          
          this.charts.displacementAbs.setOption(absOption)
        }
      },
      
      // 渲染净高图表（使用统计聚合数据 - 平均值 + 最大/最小值）
      renderClearanceChart(data) {
        if (!this.ensureChartInstance('clearance', 'clearanceChart')) return

        if (!data || data.length === 0) {
          this.charts.clearance.setOption({})
          return
        }

        // 按时间排序（统计接口返回的数据格式）
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.time ? new Date(a.time) : null
          const dateB = b.time ? new Date(b.time) : null
          if (!dateA || !dateB) return 0
          return dateA.getTime() - dateB.getTime()
        })

        // 格式化时间标签
        const times = sortedData.map(item => {
          if (item.timeLabel) {
            if (this.clearanceGranularity === 'day') {
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]}`
              }
            } else {
              const dateMatch = item.timeLabel.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
              if (dateMatch) {
                return `${dateMatch[2]}-${dateMatch[3]} ${dateMatch[4]}:${dateMatch[5]}`
              }
            }
            return item.timeLabel
          }
          if (item.time) {
            const date = new Date(item.time)
            if (this.clearanceGranularity === 'day') {
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            } else {
              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
            }
          }
          return '-'
        })

        const toFixedOrNull = (value, digits) => (typeof value === 'number' ? parseFloat(value.toFixed(digits)) : null)

        const avgValues = sortedData.map(item => toFixedOrNull(item.extraData?.avgClearance, 2))
        const maxValues = sortedData.map(item => toFixedOrNull(item.extraData?.maxClearance, 2))
        const minValues = sortedData.map(item => toFixedOrNull(item.extraData?.minClearance, 2))

        const option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                if (param.value !== null && param.value !== undefined) {
                  result += param.marker + param.seriesName + ': ' + param.value + '米<br/>'
                }
              })
              return result
            }
          },
          legend: {
            data: ['平均值', '最大值', '最小值'],
            top: '0%'
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '12%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: times,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            type: 'value',
            name: '净高(米)'
          },
          series: [
            {
              name: '平均值',
              type: 'line',
              smooth: true,
              data: avgValues,
              itemStyle: {
                color: '#0ea5e9'
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(14, 165, 233, 0.3)' },
                    { offset: 1, color: 'rgba(14, 165, 233, 0.1)' }
                  ]
                }
              }
            },
            {
              name: '最大值',
              type: 'line',
              smooth: true,
              data: maxValues,
              itemStyle: {
                color: '#ef4444'
              },
              lineStyle: {
                width: 1.5,
                type: 'dashed'
              }
            },
            {
              name: '最小值',
              type: 'line',
              smooth: true,
              data: minValues,
              itemStyle: {
                color: '#10b981'
              },
              lineStyle: {
                width: 1.5,
                type: 'dashed'
              }
            }
          ]
        }

        this.charts.clearance.setOption(option)
      },
      
      // 销毁图表
      destroyCharts() {
        if (this.charts.waterLevel) {
          this.charts.waterLevel.dispose()
          this.charts.waterLevel = null
        }
        if (this.charts.anemometer) {
          this.charts.anemometer.dispose()
          this.charts.anemometer = null
        }
        if (this.charts.visibility) {
          this.charts.visibility.dispose()
          this.charts.visibility = null
        }
        if (this.charts.flowmeter) {
          this.charts.flowmeter.dispose()
          this.charts.flowmeter = null
        }
        if (this.charts.tensionmeter) {
          this.charts.tensionmeter.dispose()
          this.charts.tensionmeter = null
        }
        if (this.charts.inclinometerAcc) {
          this.charts.inclinometerAcc.dispose()
          this.charts.inclinometerAcc = null
        }
        if (this.charts.inclinometerAngle) {
          this.charts.inclinometerAngle.dispose()
          this.charts.inclinometerAngle = null
        }
        if (this.charts.displacementX) {
          this.charts.displacementX.dispose()
          this.charts.displacementX = null
        }
        if (this.charts.displacementY) {
          this.charts.displacementY.dispose()
          this.charts.displacementY = null
        }
        if (this.charts.displacementAbs) {
          this.charts.displacementAbs.dispose()
          this.charts.displacementAbs = null
        }
        if (this.charts.clearance) {
          this.charts.clearance.dispose()
          this.charts.clearance = null
        }
      },
      
      // 分页大小变化
      handleSizeChange(tabName, val) {
        this.pagination[tabName].pageSize = val
        this.pagination[tabName].pageNum = 1
        this.loadTabData(tabName)
      },
      
      // 当前页变化
      handleCurrentChange(tabName, val) {
        this.pagination[tabName].pageNum = val
        this.loadTabData(tabName)
      },
      
      // 从数据对象中提取风速值
      extractWindSpeedFromData(item) {
        if (!item) return null
        // 根据实际API响应字段调整
        const possibleFields = ['windSpeed', 'speed', 'windSpeedMps', 'realTimeValue']
        for (const field of possibleFields) {
          if (item[field] !== undefined && item[field] !== null) {
            const value = parseFloat(item[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(1))
          }
        }
        // 如果realTimeValue是对象
        if (item.realTimeValue && typeof item.realTimeValue === 'object') {
          return this.extractWindSpeed(item.realTimeValue)
        }
        return null
      },
      
      // 从数据对象中提取风向值
      extractWindDirectionFromData(item) {
        if (!item) return null
        // 根据实际API响应字段调整
        const possibleFields = ['windDirection', 'direction', 'windDirectionDeg', 'realTimeValue']
        for (const field of possibleFields) {
          if (item[field] !== undefined && item[field] !== null) {
            const value = parseFloat(item[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(0))
          }
        }
        // 如果realTimeValue是对象
        if (item.realTimeValue && typeof item.realTimeValue === 'object') {
          return this.extractWindDirection(item.realTimeValue)
        }
        return null
      },
      
      // 提取风速值（兼容旧格式）
      extractWindSpeed(realTimeValue) {
        if (!realTimeValue) return null
        if (typeof realTimeValue === 'number') {
          return parseFloat(realTimeValue.toFixed(1))
        }
        if (typeof realTimeValue === 'object') {
          const possibleFields = ['windSpeed', 'speed', 'value', 'data', 'velocity']
          for (const field of possibleFields) {
            if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
              const value = parseFloat(realTimeValue[field])
              return isNaN(value) ? null : parseFloat(value.toFixed(1))
            }
          }
        }
        return null
      },
      
      // 提取风向值（兼容旧格式）
      extractWindDirection(realTimeValue) {
        if (!realTimeValue) return null
        if (typeof realTimeValue === 'number') {
          return parseFloat(realTimeValue.toFixed(0))
        }
        if (typeof realTimeValue === 'object') {
          const possibleFields = ['windDirection', 'direction', 'angle', 'bearing', 'heading']
          for (const field of possibleFields) {
            if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
              const value = parseFloat(realTimeValue[field])
              return isNaN(value) ? null : parseFloat(value.toFixed(0))
            }
          }
        }
        return null
      },
      
      // 格式化水位值
      formatWaterLevelValue(item) {
        if (!item) return '-'
        // 根据实际API响应字段调整
        const value = item.waterLevel || item.realTimeValue || item.value
        if (typeof value === 'number') {
          return `${parseFloat(value.toFixed(2))}米`
        }
        return '-'
      },
      
      // 格式化风速值
      formatWindSpeed(item) {
        const speed = this.extractWindSpeedFromData(item)
        return speed !== null ? `${speed}m/s` : '-'
      },
      
      // 格式化风向值
      formatWindDirection(item) {
        const direction = this.extractWindDirectionFromData(item)
        return direction !== null ? `${direction}°` : '-'
      },
      
      // 格式化能见度值
      formatVisibilityValue(value) {
        if (value === null || value === undefined) return '-'
        if (typeof value === 'number') {
          return `${parseFloat(value.toFixed(1))}`
        }
        return '-'
      },
      
      // 计算数值范围（最大值 - 最小值）
      calculateRange(values) {
        if (!values || values.length === 0) return 0
        const validValues = values.filter(v => v !== null && v !== undefined && !isNaN(v))
        if (validValues.length === 0) return 0
        const max = Math.max(...validValues)
        const min = Math.min(...validValues)
        return Math.abs(max - min)
      },
      
      // 防抖函数
      debounce(func, wait) {
        let timeout
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout)
            func(...args)
          }
          clearTimeout(timeout)
          timeout = setTimeout(later, wait)
        }
      },
      
      // 调整所有图表大小
      resizeCharts() {
        if (this.charts.waterLevel) {
          this.charts.waterLevel.resize()
        }
        if (this.charts.anemometer) {
          this.charts.anemometer.resize()
        }
        if (this.charts.visibility) {
          this.charts.visibility.resize()
        }
        if (this.charts.flowmeter) {
          this.charts.flowmeter.resize()
        }
        if (this.charts.tensionmeter) {
          this.charts.tensionmeter.resize()
        }
        if (this.charts.inclinometerAcc) {
          this.charts.inclinometerAcc.resize()
        }
        if (this.charts.inclinometerAngle) {
          this.charts.inclinometerAngle.resize()
        }
        if (this.charts.displacementX) {
          this.charts.displacementX.resize()
        }
        if (this.charts.displacementY) {
          this.charts.displacementY.resize()
        }
        if (this.charts.displacementAbs) {
          this.charts.displacementAbs.resize()
        }
        if (this.charts.clearance) {
          this.charts.clearance.resize()
        }
      }
    }
  }
  </script>
  
  <style scoped>
  /* 历史数据页面样式 */
  .history-data-page {
    padding: 20px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
    min-height: calc(100vh - 140px);
  }
  
  /* 卡片样式优化 */
  .history-data-page .bg-white {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(0, 0, 0, 0.05);
  }
  
  /* 卡片悬停效果 */
  .history-data-page .bg-white:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
    border-color: rgba(59, 130, 246, 0.2);
  }
  
  /* 图表容器 */
  .history-data-page [ref*="Chart"] {
    border-radius: 8px;
    min-height: 320px;
    width: 100%;
  }
  
  /* 图表标题图标样式 */
  .history-data-page .el-icon-data-line,
  .history-data-page .el-icon-data-analysis,
  .history-data-page .el-icon-right,
  .history-data-page .el-icon-top,
  .history-data-page .el-icon-position {
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
  
  /* 单位标签样式 */
  .history-data-page .bg-gray-100 {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  
  /* 按钮样式优化 */
  .history-data-page .el-button--primary {
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #409eff 0%, #3b82f6 100%);
    border: none;
  }
  
  .history-data-page .el-button--primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
    background: linear-gradient(135deg, #66b1ff 0%, #60a5fa 100%);
  }
  
  /* Radio按钮组样式 */
  .history-data-page .el-radio-group {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    overflow: hidden;
  }
  
  /* 表格样式 */
  .history-data-page .el-table th {
    background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%) !important;
    font-weight: 600;
    color: #333;
  }
  
  /* 控制栏样式 */
  .history-data-page .mb-4 {
    padding: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  
  /* 图标颜色增强 */
  .text-blue-500 {
    color: #3b82f6 !important;
  }
  
  .text-purple-500 {
    color: #8b5cf6 !important;
  }
  
  .text-green-500 {
    color: #10b981 !important;
  }
  
  .text-orange-500 {
    color: #f59e0b !important;
  }
  
  /* 响应式设计 */
  @media (max-width: 1200px) {
    .history-data-page {
      padding: 15px;
    }
  }
  
  @media (max-width: 768px) {
    .history-data-page {
      padding: 10px;
    }
    
    .history-data-page [ref*="Chart"] {
      min-height: 280px;
    }
  }
  </style>
  